import { CheckCircleOutline, Delete, Edit } from "@mui/icons-material";
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    TextField,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { AddressData } from "../../classes/AddressData";
import { ADDRESS } from "../../redux/apis";
import { axiosAuthInstance } from "../../redux/axiosInstance";
import CommonAlert from "../alert/CommonAlert";
import { Loader } from "../loader/loader";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";

const HEIGHT = window.innerHeight;
const WIDTH = window.innerWidth;

type Props = {
    showActions: boolean;
    setDeliveryAddress: any;
};

const Address: React.FC<Props> = ({ showActions, setDeliveryAddress }) => {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [addresses, setAddresses] = useState<AddressData[]>([]);
    const [showAlert, setShowAlert] = useState(false);
    const [showSaveAddressAlert, setShowSaveAddressAlert] = useState(false);
    const [showAddAddressForm, setShowAddAddressForm] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [addressId, setAddressId] = useState(0);
    const [fullName, setFullName] = useState("");
    const [address1, setAddress1] = useState("");
    const [address2, setAddress2] = useState("");
    const [alternatePhone, setAlternatePhone] = useState("");
    const [city, setCity] = useState("");
    const [district, setDistrict] = useState("");
    const [postal, setPostal] = useState("");
    const [landmark, setLandmark] = useState("");
    const [defau, setDefau] = useState(false);
    const [state, setState] = useState("");
    const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState<any>();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Validation errors state
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const statesAndUnionTerritories = [
        "Andhra Pradesh",
        "Arunachal Pradesh",
        "Assam",
        "Bihar",
        "Chhattisgarh",
        "Goa",
        "Gujarat",
        "Haryana",
        "Himachal Pradesh",
        "Jharkhand",
        "Karnataka",
        "Kerala",
        "Madhya Pradesh",
        "Maharashtra",
        "Manipur",
        "Meghalaya",
        "Mizoram",
        "Nagaland",
        "Odisha",
        "Punjab",
        "Rajasthan",
        "Sikkim",
        "Tamil Nadu",
        "Telangana",
        "Tripura",
        "Uttar Pradesh",
        "Uttarakhand",
        "West Bengal",
        "Andaman and Nicobar Islands",
        "Chandigarh",
        "Dadra and Nagar Haveli and Daman and Diu",
        "Delhi",
        "Lakshadweep",
        "Puducherry",
    ];

    const handleClose = () => {
        setIsLoading(false);
    };

    const resetForm = () => {
        setFullName("");
        setAddressId(0);
        setAddress1("");
        setAddress2("");
        setAlternatePhone("");
        setCity("");
        setDistrict("");
        setPostal("");
        setLandmark("");
        setState("");
        setDefau(false);
        setErrors({});
    };

    useEffect(() => {
        if (!isLoading) {
            setIsLoading(true);
        }
        getAddress();
    }, []);

    const addAddress = () => {
        setShowAddAddressForm(true);
    };

    // Validation function before saving
    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!fullName.trim()) {
            newErrors.fullName = "Full Name is required";
        }
        if (!address1.trim()) {
            newErrors.address1 = "Address Line 1 is required";
        }
        if (!city.trim()) {
            newErrors.city = "City is required";
        }
        if (!district.trim()) {
            newErrors.district = "District is required";
        }
        if (!postal) {
            newErrors.postal = "Postal Code is required";
        }
        if (!state.trim()) {
            newErrors.state = "State is required";
        }
        if (postal && !/^\d{6}$/.test(postal)) {
            newErrors.postal = "Postal code must be exactly 6 digits";
        }
        if (alternatePhone && !/^[6-9]\d{9}$/.test(alternatePhone)) {
            newErrors.alternatePhone = "Invalid phone number";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const saveAddress = async () => {
        if (!validateForm()) {
            return; // Prevent save if validation fails
        }

        let params = {
            contactId: localStorage.getItem("customerId"),
            addressId: 0,
            fullName: fullName,
            addressLine1: address1,
            addressLine2: address2,
            pinCode: postal,
            landMark: landmark,
            city: city,
            district: district,
            state: state,
            country: "INDIA",
            alternateMobileNumber: alternatePhone,
            isDefault: defau,
            addressType: "CUSTOMER_SHIPPING",
        };

        try {
            if (!isLoading) {
                setIsLoading(true);
            }
            const response = await axiosAuthInstance.post(ADDRESS, params);
            setIsLoading(false);
            if (response) {
                resetForm();
            } else {
                console.warn("Received empty response body");
            }
        } catch (error) {
            setIsLoading(false);
            console.error("Error adding address:", error);
        }
        getAddress();
        setShowAddAddressForm(false);
        setShowSaveAddressAlert(true);
        setTimeout(() => {
            setShowSaveAddressAlert(false);
        }, 2000);
    };

    const handleDelete = (addressId: any) => {
        setSelectedDeliveryAddress(addressId);
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setSelectedDeliveryAddress(null);
    };

    const deleteAddress = async () => {
        setIsModalVisible(false);
        if (!isLoading) {
            setIsLoading(true);
        }
        try {
            const response = await axiosAuthInstance.delete(
                `${ADDRESS}/${selectedDeliveryAddress}`
            );
            setAddresses(
                addresses.filter((address) => address.addressId !== selectedDeliveryAddress)
            );
            setShowAlert(true);
            setTimeout(() => {
                setShowAlert(false);
            }, 2000);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            console.error("Error deleting address:", error);
        }
    };

    const getAddress = async () => {
        const id = localStorage.getItem("customerId") || "0";
        const addressType = "CUSTOMER_SHIPPING";

        try {
            const response: any = await axiosAuthInstance.get(
                `${ADDRESS}/${id}?addressType=${encodeURIComponent(addressType)}`
            );
            if (response) {
                setIsLoading(false);
                setAddresses(response.data);
            } else {
                console.warn("Empty response");
            }
        } catch (error) {
            setIsLoading(false);
            console.error("Error:", error);
        }
    };

    const editAddress = (address: AddressData) => {
        setIsEdit(true);
        setShowAddAddressForm(true);
        setAddressId(address.addressId);
        setFullName(address.fullName);
        setAddress1(address.addressLine1);
        setAddress2(address.addressLine2);
        setAlternatePhone(address.alternateMobileNumber);
        setCity(address.city);
        setDistrict(address.district);
        setPostal(address.pinCode);
        setLandmark(address.landMark);
        setState(address.state);
        setDefau(address.isDefault ? true : false);
        setErrors({});
    };

    const updateEditedAddress = async (addressId: number) => {
        if (!validateForm()) {
            return; // Prevent update if validation fails
        }

        let params = {
            contactId: localStorage.getItem("customerId"),
            addressId: addressId,
            fullName: fullName,
            addressLine1: address1,
            addressLine2: address2,
            pinCode: postal,
            landMark: landmark,
            city: city,
            district: district,
            state: state,
            country: "INDIA",
            alternateMobileNumber: alternatePhone,
            isDefault: defau,
            addressType: "CUSTOMER_SHIPPING",
        };
        updateAddress(params);
    };

    const setDefaultAddress = (address: any) => {
        address = { ...address, isDefault: true };
        updateAddress(address);
    };

    const updateAddress = async (address: any) => {
        try {
            if (!isLoading) {
                setIsLoading(true);
            }
            const updateUrl = `${ADDRESS}/${address.addressId}`;
            const response = await axiosAuthInstance.put(updateUrl, address);
            setIsLoading(false);
            if (response) {
                resetForm();
            } else {
                console.warn("Received empty response body");
            }
        } catch (error) {
            setIsLoading(false);
            console.error("Error adding address:", error);
        }
        getAddress();
        setShowAddAddressForm(false);
        setShowSaveAddressAlert(true);
        setTimeout(() => {
            setShowSaveAddressAlert(false);
        }, 2000);
    };

    const handleClick = (addressId: any) => {
        if (!showActions) {
            // means from Confirm Page
            setSelectedDeliveryAddress(addressId);
            setDeliveryAddress(addressId);
        }
    };

    return (
        <>
            <Loader loading={isLoading} handleClose={handleClose} />
            <Box
                style={{
                    borderRadius: 14,
                    backgroundColor: "#f8f3f3",
                    border: "1px solid #7a7272",
                }}
            >
                <Box p={3} mx="auto">
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={12} style={{ display: "flex" }}>
                            <img src={require("../../../assets/maps.png")} alt="map" />
                            <Typography variant="h5" style={{ marginLeft: "20px" }}>
                                Shipping Address
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <Typography variant="subtitle2" style={{ marginLeft: "50px" }}>
                                You can add a New Shipping address or can Modify the Existing one
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Box p={3} pt={0} mx="auto">
                    {showAlert && (
                        <CommonAlert
                            message="Address deleted successfully."
                            variant="standard"
                            severity="success"
                            onClose={() => {
                                setShowAlert(false);
                            }}
                        />
                    )}
                    {showSaveAddressAlert && (
                        <CommonAlert
                            message="Address saved successfully."
                            variant="filled"
                            severity="success"
                            onClose={() => {
                                setShowSaveAddressAlert(false);
                            }}
                        />
                    )}
                    <List>
                        {addresses &&
                            addresses.map((address) => (
                                <React.Fragment key={address.addressId}>
                                    <ListItem
                                        sx={{
                                            border:
                                                showActions && address.isDefault
                                                    ? "1px solid #0a3152"
                                                    : !showActions &&
                                                        selectedDeliveryAddress &&
                                                        selectedDeliveryAddress === address.addressId
                                                        ? "3px solid green"
                                                        : "1px solid #ccc",
                                            borderRadius: 2,
                                            mb: 2,
                                            backgroundColor: "#f9f9f9",
                                            cursor: !showActions ? "pointer" : "default",
                                        }}
                                        onClick={(e) => handleClick(address.addressId)}
                                    >
                                        <ListItemText
                                            sx={{}}
                                            primaryTypographyProps={{
                                                style: {
                                                    fontWeight: "bold",
                                                    color: "##0A3251",
                                                },
                                            }}
                                            primary={`${address.fullName}`}
                                            secondary={`${address.addressLine1}, ${address.city}, ${address.district}, ${address.state}, ${address.pinCode}`}
                                        />
                                        {showActions && (
                                            <>
                                                <Typography
                                                    variant="body2"
                                                    color="primary"
                                                    sx={{ marginRight: 1 }}
                                                >
                                                    {address.isDefault ? "Default" : ""}
                                                </Typography>
                                                <IconButton
                                                    edge="end"
                                                    color="primary"
                                                    onClick={() => editAddress(address)}
                                                    sx={{ marginRight: 1 }}
                                                >
                                                    <Edit style={{ color: " #0a3150" }} />
                                                </IconButton>
                                                <IconButton
                                                    edge="end"
                                                    color="error"
                                                    onClick={() => handleDelete(address.addressId)}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </>
                                        )}
                                    </ListItem>
                                    <Divider />
                                </React.Fragment>
                            ))}
                    </List>

                    <Box display="flex" justifyContent="right" alignItems="center" mb={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            style={{ backgroundColor: "#0a3150", color: "white" }}
                            onClick={addAddress}
                        >
                            Add New Address
                        </Button>
                    </Box>
                    {showAddAddressForm && (
                        <Box
                            component="form"
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { sm: "1fr 1fr" },
                                backgroundColor: "white",
                                gap: 2,
                                p: 4,
                                mt: 2,
                            }}
                            noValidate
                            autoComplete="off"
                        >
                            <TextField
                                label="Full Name"
                                value={fullName}
                                required
                                onChange={(event) => setFullName(event.target.value)}
                                fullWidth
                                error={!!errors.fullName}
                                helperText={errors.fullName}
                            />
                            <TextField
                                label="Enter Flat, House No.,"
                                value={address1}
                                required
                                onChange={(event) => setAddress1(event.target.value)}
                                fullWidth
                                error={!!errors.address1}
                                helperText={errors.address1}
                            />
                            <TextField
                                label="Enter Building, Company"
                                value={address2}
                                onChange={(event) => setAddress2(event.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Enter Alternate Phone Number"
                                value={alternatePhone}
                                onChange={(event) => setAlternatePhone(event.target.value)}
                                fullWidth
                                error={!!errors.alternatePhone}
                                helperText={errors.alternatePhone}
                            />
                            <TextField
                                label="Landmark"
                                value={landmark}
                                onChange={(event) => setLandmark(event.target.value)}
                                fullWidth
                            />
                            <Grid container spacing={2}>
                                <Grid item xs={6} md={6}>
                                    <TextField
                                        label="City"
                                        value={city}
                                        onChange={(event) => setCity(event.target.value)}
                                        fullWidth
                                        required
                                        error={!!errors.city}
                                        helperText={errors.city}
                                    />
                                </Grid>
                                <Grid item xs={6} md={6}>
                                    <TextField
                                        label="District"
                                        value={district}
                                        required
                                        onChange={(event) => setDistrict(event.target.value)}
                                        fullWidth
                                        error={!!errors.district}
                                        helperText={errors.district}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item xs={6} md={6}>
                                    <TextField
                                        label="Postal Code"
                                        value={postal}
                                        onChange={(event) => setPostal(event.target.value)}
                                        fullWidth
                                        required
                                        error={!!errors.postal}
                                        helperText={errors.postal}
                                    />
                                </Grid>
                                <Grid item xs={6} md={6}>
                                    <TextField
                                        select
                                        SelectProps={{ native: true }}
                                        fullWidth
                                        variant="outlined"
                                        value={state}
                                        required
                                        error={!!errors.state}
                                        helperText={errors.state}
                                        onChange={(event) => setState(event.target.value)}
                                    >
                                        <option value="">Select State</option>
                                        {statesAndUnionTerritories.map((state, index) => (
                                            <option key={index} value={state}>
                                                {state}
                                            </option>
                                        ))}
                                    </TextField>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item xs={6} md={6}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={defau}
                                                onChange={(event) => setDefau(event.target.checked)}
                                                name="checked"
                                                color="primary"
                                            />
                                        }
                                        label="Set as default shipping address"
                                    />
                                </Grid>
                            </Grid>

                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                sx={{ mt: 2, mb: 2, backgroundColor: "#0a3150", color: "white" }}
                                onClick={isEdit ? () => updateEditedAddress(addressId) : saveAddress}
                            >
                                SAVE
                            </Button>
                            <Button
                                variant="contained"
                                fullWidth
                                sx={{ mt: 2, mb: 2, backgroundColor: "#c92b22", color: "white" }}
                                onClick={() => {
                                    setShowAddAddressForm(false);
                                    setErrors({});
                                }}
                            >
                                CANCEL
                            </Button>
                        </Box>
                    )}
                </Box>
                <ConfirmationModal
                    isVisible={isModalVisible}
                    onConfirm={deleteAddress}
                    onCancel={handleCancel}
                    message={`Are you sure to delete this address ?`}
                />
            </Box>
        </>
    );
};

export default Address;
